import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import './GlobalSearch.css';

/**
 * Search result type → tab navigation mapping.
 * Configurable: add new types here when new content types are added.
 */
const SEARCH_TYPE_CONFIG = {
  crm_company: {
    getTabId: (item) => `customer-${item.id}`,
    getTabTitle: (item) => item.title,
    getTabPath: (item) => `/customer/${item.id}`,
    component: 'CustomerDetail',
    getData: (item) => ({ customerId: item.id }),
  },
  medium: {
    getTabId: (item) => `media-detail-${item.id}`,
    getTabTitle: (item) => item.title,
    getTabPath: (item) => `/media/${item.id}`,
    component: 'MediaPricelistDetail',
    getData: (item) => ({ mediaId: item.id }),
  },
  project: {
    getTabId: (item) => `media-detail-${item.parentId || item.id}`,
    getTabTitle: (item) => item.parentName || item.title,
    getTabPath: (item) => `/media/${item.parentId || item.id}`,
    component: 'MediaPricelistDetail',
    getData: (item) => ({ mediaId: item.parentId || item.id, openProjectId: item.id }),
  },
  ratecard: {
    getTabId: (item) => `pricelist-items-${item.id}`,
    getTabTitle: (item) => `Ceník: ${item.title}`,
    getTabPath: (item) => `/media-pricelist/${item.parentId}/pricelist/${item.id}`,
    component: 'PricelistItems',
    getData: (item) => ({
      pricelistId: String(item.id),
      pricelistName: item.title,
      mediaName: item.parentName || '',
    }),
  },
  order: {
    getTabId: (item) => `order-edit-${item.id}`,
    getTabTitle: (item) => item.title,
    getTabPath: (item) => `/orders/${item.id}/edit`,
    component: 'OrderForm',
    getData: (item) => ({ orderId: item.id }),
  },
};

/**
 * Category tile → tab navigation mapping.
 */
const CATEGORY_NAV = {
  'CRM': {
    tabId: 'customers',
    title: 'Zákazník',
    path: '/customers',
    component: 'CustomerManagement',
    icon: 'bi-building',
  },
  'Média': {
    tabId: 'media-pricelist',
    title: 'Média a ceníky',
    path: '/media-pricelist',
    component: 'MediaPricelist',
    icon: 'bi-newspaper',
  },
  'Projekty': {
    tabId: 'media-pricelist',
    title: 'Média a ceníky',
    path: '/media-pricelist',
    component: 'MediaPricelist',
    icon: 'bi-folder',
  },
  'Ceníky': {
    tabId: 'media-pricelist',
    title: 'Média a ceníky',
    path: '/media-pricelist',
    component: 'MediaPricelist',
    icon: 'bi-receipt',
  },
  'Objednávky': {
    tabId: 'orders',
    title: 'Objednávky',
    path: '/orders',
    component: 'OrdersList',
    icon: 'bi-receipt-cutoff',
  },
};

function GlobalSearch({ openTab }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Total items for keyboard navigation: categories + results
  const totalItems = categories.length + results.length;

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setCategories([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.globalSearch(searchQuery, 10);
      const data = response.data || response;
      setCategories(data.categories || []);
      setResults(data.results || []);
      setIsOpen(true);
      setHighlightIndex(-1);
    } catch (err) {
      console.error('Global search error:', err);
      setResults([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 1000);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Navigate to a search result
  const handleResultClick = (item) => {
    const config = SEARCH_TYPE_CONFIG[item.type];
    if (!config) return;

    openTab(
      config.getTabId(item),
      config.getTabTitle(item),
      config.getTabPath(item),
      config.component,
      config.getData(item),
    );

    setQuery('');
    setIsOpen(false);
    setResults([]);
    setCategories([]);
    inputRef.current?.blur();
  };

  // Navigate to a category
  const handleCategoryClick = (categoryLabel) => {
    const nav = CATEGORY_NAV[categoryLabel];
    if (!nav) return;

    openTab(nav.tabId, nav.title, nav.path, nav.component);

    setQuery('');
    setIsOpen(false);
    setResults([]);
    setCategories([]);
    inputRef.current?.blur();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' && query.length >= 2) {
        performSearch(query);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0) {
          if (highlightIndex < categories.length) {
            handleCategoryClick(categories[highlightIndex]);
          } else {
            handleResultClick(results[highlightIndex - categories.length]);
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Focus handler
  const handleFocus = () => {
    if (query.length >= 2 && (results.length > 0 || categories.length > 0)) {
      setIsOpen(true);
    }
  };

  // Group results by category for display
  const groupedResults = results.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Highlight matching text
  const highlightMatch = (text, searchQuery) => {
    if (!text || !searchQuery) return text;
    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i}>{part}</mark> : part
    );
  };

  let flatIndex = -1;

  return (
    <div className="global-search-wrapper" ref={wrapperRef}>
      <div className="global-search-input-wrapper">
        <i className="bi bi-search global-search-icon" />
        <input
          ref={inputRef}
          type="search"
          className="form-control form-control-sm global-search-input"
          placeholder="Hledat v CRM, médiích, cenících, objednávkách…"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          autoComplete="off"
        />
        {loading && <span className="global-search-spinner" />}
      </div>

      {isOpen && (categories.length > 0 || results.length > 0) && (
        <div className="global-search-dropdown">
          {/* Category tiles */}
          {categories.length > 0 && (
            <div className="gs-section">
              <div className="gs-section-label">Sekce</div>
              <div className="gs-categories">
                {categories.map((cat) => {
                  flatIndex++;
                  const idx = flatIndex;
                  const nav = CATEGORY_NAV[cat];
                  return (
                    <div
                      key={cat}
                      className={`gs-category-tile ${highlightIndex === idx ? 'gs-highlighted' : ''}`}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      <i className={`bi ${nav?.icon || 'bi-grid'}`} />
                      <span>{cat}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Grouped results */}
          {Object.entries(groupedResults).map(([category, items]) => (
            <div key={category} className="gs-section">
              <div className="gs-section-label">{category}</div>
              {items.map((item) => {
                flatIndex++;
                const idx = flatIndex;
                return (
                  <div
                    key={`${item.type}-${item.id}`}
                    className={`gs-result-item ${highlightIndex === idx ? 'gs-highlighted' : ''}`}
                    onClick={() => handleResultClick(item)}
                  >
                    <div className="gs-result-icon">
                      <i className={`bi ${item.icon}`} />
                    </div>
                    <div className="gs-result-content">
                      <div className="gs-result-title">
                        {highlightMatch(item.title, query)}
                      </div>
                      <div className="gs-result-meta">
                        {item.subtitle && (
                          <span className="gs-result-subtitle">
                            {highlightMatch(item.subtitle, query)}
                          </span>
                        )}
                        {item.parentName && (
                          <span className="gs-result-parent">
                            <i className="bi bi-arrow-right-short" />
                            {item.parentName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="gs-result-badge">
                      {item.category}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* No results message */}
          {results.length === 0 && categories.length === 0 && (
            <div className="gs-no-results">
              Žádné výsledky pro „{query}"
            </div>
          )}

          {/* Footer hint */}
          <div className="gs-footer">
            <span><kbd>↑</kbd> <kbd>↓</kbd> navigace</span>
            <span><kbd>Enter</kbd> otevřít</span>
            <span><kbd>Esc</kbd> zavřít</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
